/**
 * The POS's dialog renderer. The service is shared; the markup is not — the dashboard renders
 * the same requests with MUI.
 *
 * Only the topmost dialog is `active`, so its focus trap does not fight the one beneath it.
 */

import { useState } from 'react';

import { settle, useDialogStack, type DialogRequest } from '@pos/shared';

import { Button, Field, Sheet } from '@/shared/ui';

export function DialogHost() {
  const stack = useDialogStack();

  return (
    <>
      {stack.map((request, index) => (
        <DialogView key={request.id} request={request} active={index === stack.length - 1} />
      ))}
    </>
  );
}

function DialogView({ request, active }: { request: DialogRequest; active: boolean }) {
  const { options } = request;

  const dismiss = () => {
    settle(request.id, request.kind === 'alert' ? true : null);
  };

  if (request.kind === 'prompt') {
    return <PromptView request={request} active={active} onDismiss={dismiss} />;
  }

  return (
    <Sheet
      open
      active={active}
      onDismiss={dismiss}
      title={options.title}
      dismissOnBackdrop={request.kind === 'confirm' ? (request.options.dismissOnBackdrop ?? true) : true}
      footer={
        <>
          {request.kind === 'confirm' && (
            <Button variant="outline" fullWidth onClick={dismiss}>
              {request.options.cancelLabel ?? 'Cancel'}
            </Button>
          )}
          {/* Never red: the one red button allowed in the product is "Remove N units" on the
              dashboard. Signing out, reversing and cancelling are not destructions (case 169). */}
          <Button
            variant="action"
            fullWidth
            onClick={() => {
              settle(request.id, true);
            }}
          >
            {options.confirmLabel ?? 'Confirm'}
          </Button>
        </>
      }
    >
      {options.body !== undefined && (
        <p className="text-base leading-relaxed text-content-secondary">{options.body}</p>
      )}
    </Sheet>
  );
}

function PromptView({
  request,
  active,
  onDismiss,
}: {
  request: Extract<DialogRequest, { kind: 'prompt' }>;
  active: boolean;
  onDismiss: () => void;
}) {
  const { options } = request;
  const [value, setValue] = useState(options.initialValue ?? '');
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const message = options.validate?.(value) ?? null;
    if (message !== null) {
      setError(message);
      return;
    }

    settle(request.id, value);
  };

  return (
    <Sheet
      open
      active={active}
      onDismiss={onDismiss}
      title={options.title}
      dismissOnBackdrop={false}
      footer={
        <>
          <Button variant="outline" fullWidth onClick={onDismiss}>
            {options.cancelLabel ?? 'Cancel'}
          </Button>
          <Button
            variant="action"
            fullWidth
            onClick={submit}
          >
            {options.confirmLabel ?? 'Save'}
          </Button>
        </>
      }
    >
      {options.body !== undefined && (
        <p className="mb-4 text-base leading-relaxed text-content-secondary">{options.body}</p>
      )}

      <Field
        label={options.label}
        placeholder={options.placeholder ?? ''}
        value={value}
        error={error}
        autoFocus
        onChange={(event) => {
          setValue(event.target.value);
          if (error !== null) setError(null);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && options.multiline !== true) submit();
        }}
      />
    </Sheet>
  );
}
